"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const sbc_core_authentication_ui_1 = require("@sage/sbc.core.authentication.ui");
const createConfig_1 = tslib_1.__importDefault(require("../config/createConfig"));
const APIStatusCode_1 = tslib_1.__importDefault(require("../constants/APIStatusCode"));
const Header_1 = tslib_1.__importDefault(require("../constants/Header"));
const HttpStatusCode_1 = tslib_1.__importDefault(require("../constants/HttpStatusCode"));
const QueryParameter_1 = tslib_1.__importDefault(require("../constants/QueryParameter"));
const createLogger_1 = tslib_1.__importDefault(require("../logger/createLogger"));
const HttpError_1 = tslib_1.__importDefault(require("./HttpError"));
const logHttpErrorWithSentry_1 = require("./logHttpErrorWithSentry");
const logMessageWithSentry_1 = require("./logMessageWithSentry");
const { config } = (0, createConfig_1.default)();
const isBool = (v) => typeof v === "boolean";
const getCSRFToken = (url) => {
    const token = document.querySelector('meta[name="csrf-token"]');
    if (token && url.includes("mfe_proxy")) {
        return token.content;
    }
    return null;
};
const Transformer = {
    getToken: async () => (0, sbc_core_authentication_ui_1.getTokenSilently)(),
    getHeaders: (method, url, token, options) => {
        const returnedHeaders = new Headers(options?.headers);
        const CSRFToken = getCSRFToken(url);
        if (CSRFToken) {
            returnedHeaders.set("credentials", "same-origin");
            returnedHeaders.set("X-CSRF-Token", CSRFToken);
        }
        else if (token) {
            returnedHeaders.set(Header_1.default.AUTHORIZATION, `Bearer ${token}`);
        }
        if (options?.useCache) {
            returnedHeaders.set(Header_1.default.CACHE.RESPONSE.NAME, Header_1.default.CACHE.RESPONSE.VALUE);
        }
        if (options?.useMSW) {
            returnedHeaders.set(Header_1.default.MSW.USE.NAME, Header_1.default.MSW.USE.VALUE);
        }
        if (options?.idempotencyKey) {
            returnedHeaders.set(Header_1.default.IDEMPOTENCY, options?.idempotencyKey);
        }
        if (options?.useTryWaitForResult) {
            returnedHeaders.set(Header_1.default.TRY_WAIT_FOR_RESULT.NAME, Header_1.default.TRY_WAIT_FOR_RESULT.VALUE);
        }
        if (method !== "GET" && !returnedHeaders.has(Header_1.default.IDEMPOTENCY)) {
            throw new Error("x-idempotency-key header needs to be passed for post, put, patch, delete request.");
        }
        if (["POST", "PUT", "PATCH"].includes(method))
            returnedHeaders.set(Header_1.default.CONTENT_TYPE.NAME, Header_1.default.CONTENT_TYPE.VALUE.JSON);
        if (!returnedHeaders.has(Header_1.default.TRANSLATION.NAME)) {
            returnedHeaders.set(Header_1.default.TRANSLATION.NAME, Header_1.default.TRANSLATION.VALUE.DEFAULT);
        }
        if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
            const utils = config("features.sbc.core.utils.ui.useTryWaitForResult");
            const mfeName = returnedHeaders.get("x-mfe-origin");
            const mfe = mfeName
                ? config(`features.${mfeName}.useTryWaitForResult`)
                : null;
            const request = options?.useTryWaitForResult;
            let result = false;
            if (isBool(request)) {
                result = request;
            }
            else if (isBool(mfe)) {
                result = mfe;
            }
            else if (isBool(utils)) {
                result = utils;
            }
            if (result && !returnedHeaders.has(Header_1.default.TRY_WAIT_FOR_RESULT.NAME)) {
                returnedHeaders.set(Header_1.default.TRY_WAIT_FOR_RESULT.NAME, Header_1.default.TRY_WAIT_FOR_RESULT.VALUE);
            }
        }
        return returnedHeaders;
    },
    setQueryParameters: (url, queryParameters) => {
        if (queryParameters && queryParameters.length > 0) {
            const targetUrl = new URL(url);
            const { searchParams } = targetUrl;
            queryParameters.forEach((element) => {
                const { name, value, overwrite = true } = element;
                if (overwrite) {
                    // Set the parameter, replacing existing ones with the same key
                    searchParams.set(name, value);
                }
                else {
                    // Append the parameter without replacing existing ones
                    searchParams.append(name, value);
                }
            });
            const returnedUrl = `${targetUrl.protocol}//${targetUrl.host}${targetUrl.pathname === "/" ? "" : targetUrl.pathname}?${searchParams.toString()}`;
            return returnedUrl;
        }
        return url;
    },
    createRequestInfo: async (method, url, options) => {
        let token;
        try {
            token = await Transformer.getToken();
        }
        catch {
            // On error we do nothing, we just not add the Authorization header
        }
        const fullUrl = Transformer.setQueryParameters(url, options?.queryParameters);
        const requestInit = {
            method,
            headers: Transformer.getHeaders(method, url, token, options),
            body: options?.body ? JSON.stringify(options?.body) : undefined,
            signal: options?.signal,
        };
        return {
            fullUrl,
            init: requestInit,
            preventRetryOnStatus: options?.preventRetryOnStatus,
            maxRetries: options?.maxRetries,
        };
    },
    checkErrorStatus: async (response, requestInfo) => {
        if (response.status < HttpStatusCode_1.default.OK || response.status > 299) {
            (0, createLogger_1.default)().error(new Error(response.statusText), {
                type: "apiError",
                level: "high",
                httpStatus: response.status,
                apiUrl: requestInfo.fullUrl,
                xCorrelationId: response.headers.get(Header_1.default.CORRELATION),
            });
            const errorBody = await Transformer.getBody(response);
            const error = new HttpError_1.default(response.status, response.statusText, errorBody, response.headers, errorBody?.Code);
            (0, logHttpErrorWithSentry_1.logHttpErrorWithSentry)({ error, requestInfo, response, errorBody });
            throw error;
        }
    },
    wait: (delay) => {
        return new Promise((resolve) => {
            setTimeout(resolve, delay);
        });
    },
    handleRetryAfter: async (requestInfo, response, attemptNumber = 1) => {
        const allowedRetryStatus = [
            HttpStatusCode_1.default.ACCEPTED,
            HttpStatusCode_1.default.TIMEOUT,
            HttpStatusCode_1.default.LOCKED,
            HttpStatusCode_1.default.TOO_MANY_REQUESTS,
            HttpStatusCode_1.default.BAD_GATEWAY,
            HttpStatusCode_1.default.SERVER_NOT_AVAILABLE,
            HttpStatusCode_1.default.INTERNAL_SERVER_ERROR,
        ];
        const allowedBackOffStatus = [
            HttpStatusCode_1.default.TIMEOUT,
            HttpStatusCode_1.default.LOCKED,
            HttpStatusCode_1.default.BAD_GATEWAY,
            HttpStatusCode_1.default.SERVER_NOT_AVAILABLE,
            HttpStatusCode_1.default.INTERNAL_SERVER_ERROR,
        ];
        const preventRetryOnStatus = requestInfo.preventRetryOnStatus ?? [];
        const maxRetries = requestInfo.maxRetries ?? 3;
        if (response.status === HttpStatusCode_1.default.FORBIDDEN) {
            const responseBody = await Transformer.getBody(response.clone());
            if (responseBody?.Code === APIStatusCode_1.default.INVALID_TENANT) {
                allowedRetryStatus.push(HttpStatusCode_1.default.FORBIDDEN);
            }
        }
        if (response.status === HttpStatusCode_1.default.TOO_MANY_REQUESTS &&
            !response.headers.get(Header_1.default.RETRY_AFTER)) {
            allowedBackOffStatus.push(HttpStatusCode_1.default.TOO_MANY_REQUESTS);
        }
        let retryAfter = parseInt(response.headers.get(Header_1.default.RETRY_AFTER) || "1", 10);
        if (Number.isNaN(retryAfter)) {
            retryAfter = 1;
        }
        if (allowedRetryStatus.includes(response.status) &&
            !preventRetryOnStatus.includes(response.status) &&
            attemptNumber <= maxRetries) {
            await Transformer.wait(retryAfter *
                1000 *
                (allowedBackOffStatus.includes(response.status) ? attemptNumber : 1));
            const nextResponse = await fetch(requestInfo.fullUrl, requestInfo.init);
            if (allowedRetryStatus.includes(HttpStatusCode_1.default.FORBIDDEN)) {
                allowedRetryStatus.splice(allowedRetryStatus.indexOf(HttpStatusCode_1.default.FORBIDDEN), 1);
            }
            if (nextResponse.status === HttpStatusCode_1.default.FORBIDDEN) {
                const responseBody = await Transformer.getBody(nextResponse.clone());
                if (responseBody?.Code === APIStatusCode_1.default.INVALID_TENANT) {
                    allowedRetryStatus.push(HttpStatusCode_1.default.FORBIDDEN);
                }
            }
            if (!allowedRetryStatus.includes(nextResponse.status)) {
                // while we are retrying we don't throw an exception for allowed retry status
                await Transformer.checkErrorStatus(nextResponse, requestInfo);
            }
            return Transformer.handleRetryAfter(requestInfo, nextResponse, attemptNumber + 1);
        }
        await Transformer.checkErrorStatus(response, requestInfo);
        return response;
    },
    fetchPaginated: async (url, options) => {
        const request = await Transformer.createRequestInfo("GET", url, options);
        const response = await Transformer.fetch(request);
        const { totalPages } = Transformer.getPaginationHeaders(response.headers);
        if (totalPages && totalPages > 1) {
            const queryParameters = options?.queryParameters ?? [];
            const promises = [];
            for (let page = 2; page <= totalPages; page += 1) {
                const fullUrl = Transformer.setQueryParameters(url, [
                    ...queryParameters,
                    {
                        name: QueryParameter_1.default.PAGINATION.PAGE,
                        value: page.toString(),
                    },
                ]);
                const followingRequest = { ...request, fullUrl };
                promises.push(Transformer.fetch(followingRequest));
            }
            const responses = await Promise.all(promises);
            const results = responses.map((nextResponse) => nextResponse.body);
            const flattenResults = results.reduce((previousResult, currentResult) => previousResult.concat(currentResult), []);
            response.body.push(...flattenResults);
        }
        // Pagination object is removed because it doesn't make sense to keep it here
        return {
            ...response,
            pagination: undefined,
        };
    },
    fetch: async (requestInfo) => {
        let response = await fetch(requestInfo.fullUrl, requestInfo.init);
        response = await Transformer.handleRetryAfter(requestInfo, response);
        const transformedResponse = await Transformer.transform(requestInfo, response);
        (0, logMessageWithSentry_1.checkForDeprecatedAPI)({ requestInfo, response });
        return transformedResponse;
    },
    transform: async (requestInfo, response) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const body = await Transformer.getBody(response);
        const transformedResponse = {
            status: Number(response.status),
            statusText: response.statusText,
            body,
            headers: response.headers,
            pagination: Transformer.getPagination(requestInfo.fullUrl, response.headers),
        };
        return transformedResponse;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getBody: async (response) => {
        const contentType = response.headers.get(Header_1.default.CONTENT_TYPE.NAME);
        if (response.status === HttpStatusCode_1.default.NO_CONTENT || !contentType)
            // If the response has no content, no need to read an empty stream
            return undefined;
        if (contentType.indexOf(Header_1.default.CONTENT_TYPE.VALUE.JSON) > -1)
            return response.json();
        if (Header_1.default.CONTENT_TYPE.VALUE.FILES.includes(contentType))
            return response.blob();
        return response.text();
    },
    getPaginationHeaders: (headers) => {
        const totalPages = headers.get(Header_1.default.PAGINATION.TOTAL_PAGES);
        const currentPage = headers.get(Header_1.default.PAGINATION.CURRENT_PAGE);
        return {
            currentPage: currentPage ? +currentPage : undefined,
            totalPages: totalPages ? +totalPages : undefined,
        };
    },
    getPagination: (url, headers) => {
        const { totalPages, currentPage } = Transformer.getPaginationHeaders(headers);
        if (!currentPage || !totalPages) {
            return undefined;
        }
        let previousPage;
        let nextPage;
        if (currentPage > 1) {
            previousPage = Transformer.setQueryParameters(url, [
                {
                    name: QueryParameter_1.default.PAGINATION.PAGE,
                    value: (currentPage - 1).toString(),
                },
            ]);
        }
        if (currentPage < totalPages) {
            nextPage = Transformer.setQueryParameters(url, [
                {
                    name: QueryParameter_1.default.PAGINATION.PAGE,
                    value: (currentPage + 1).toString(),
                },
            ]);
        }
        return { previousPage, nextPage };
    },
};
exports.default = Transformer;
//# sourceMappingURL=Transformer.js.map